import { AppNS } from 'app';
import { request } from 'app/api_v2/config';
import axios, { Canceler } from 'axios';

import { API_URL } from '../../core/config';
import { ApiNS } from '../api';
import { getErrorMessage } from '../helpers/errorHandling';
import {
  parseNewEventResponse,
  parseProjectLead,
  parseProposal,
  prepareBookingData,
  prepareEventReportData,
} from './agency-jobs-parsers';

const URL = 'jobs';
const LEADS_URL = 'job_leads';
const EVENTS_URL = 'reports';
const BOOKINGS_URL = 'bookings';
const STATE_URL = 'change-state';
const JOB_CHANGES_URL = 'job-changes';
const REQUEST_CLOSING_URL = 'permits/request-closing';
const CHECKOUTS = 'checkouts';
const JOB_DOCUMENTS = 'job-documents';

const CancelToken = axios.CancelToken;
let cancel: Canceler;

export function apiGetProjectLeadsForListing(
  type: string,
  params: AppNS.Params,
  signal?: AbortSignal
): Promise<AppNS.GetProjectLeadsApiResponse> {
  if (type === 'job-opportunities') {
    return apiGetOpportunitiesForListing(
      { ...params, states: ['pending', 'proposal_sent', 'proposal_requested'] },
      signal
    );
  }

  if (type === 'confirmed-jobs') {
    return apiGetBookingsForListing({ ...params, states: ['confirmed'] }, signal);
  }

  return apiGetBookingsForListing(
    {
      ...params,
      states: ['past', 'cancelled_by_agency', 'cancelled_by_client', 'cancelled_by_admin'],
    },
    signal
  );
}

function apiGetOpportunitiesForListing(
  params: AppNS.Params,
  signal?: AbortSignal
): Promise<AppNS.GetProjectLeadsApiResponse> {
  return request(
    {
      url: `${LEADS_URL}`,
      method: 'GET',
      baseURL: API_URL,
      params: {
        ...params,
        per_page: params.perPage || 20,
      },
      signal,
    },
    (data) => {
      return {
        ...data,
        results: data.results
          .filter((item: AppNS.ProjectLead) => item.job)
          .map((item: AppNS.ProjectLead) => {
            return parseProjectLead(item);
          }),
      };
    }, // old errors format
    { errorType: 'legacy' }
  );
}

function apiGetBookingsForListing(
  params: AppNS.Params,
  signal?: AbortSignal
): Promise<AppNS.GetProjectLeadsApiResponse> {
  return request(
    {
      url: `${BOOKINGS_URL}`,
      method: 'GET',
      baseURL: API_URL,
      params: {
        ...params,
        per_page: params.perPage || 20,
      },
      signal,
    },
    (data) => {
      return {
        ...data,
        results: data.results.map((item: AppNS.ProjectLead) => {
          return parseProjectLead(item);
        }),
      };
    }, // old errors format
    { errorType: 'legacy' }
  );
}

export function apiGetProjectLeads(
  type: string,
  searchQuery: string,
  page: number,
  searchBy: string,
  projectType: string,
  agencyCompanyId: string
): Promise<AppNS.GetProjectLeadsApiResponse> {
  if (cancel !== undefined) {
    cancel();
  }
  if (type === 'job-opportunities') {
    return apiGetOpportunities(searchQuery, page, searchBy, projectType, agencyCompanyId);
  } else if (type === 'confirmed-jobs') {
    return apiGetBookings(searchQuery, ['confirmed'], page, searchBy, projectType, agencyCompanyId);
  } else {
    return apiGetBookings(
      searchQuery,
      ['past', 'cancelled_by_agency', 'cancelled_by_client', 'cancelled_by_admin'],
      page,
      searchBy,
      projectType,
      agencyCompanyId
    );
  }
}

export function apiGetProjectLead(type: string, id: string): Promise<AppNS.ProjectLead> {
  if (cancel !== undefined) {
    cancel();
  }

  if (type === 'job-opportunities') {
    return apiGetProjectLeadById(id);
  } else {
    return apiGetJobBookingById(id);
  }
}

function apiGetOpportunities(
  searchQuery: string,
  page: number,
  searchBy: string,
  projectType: string,
  agencyCompanyId: string
): Promise<AppNS.GetProjectLeadsApiResponse> {
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${LEADS_URL}`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
        params: {
          query: searchQuery,
          search_by: searchBy,
          job_type: projectType,
          states: ['pending', 'proposal_sent', 'proposal_requested'],
          agency_company_id: agencyCompanyId,
          page,
        },
      })
      .then(({ data }) => {
        if (data.results) {
          resolve({
            ...data,
            results: data.results
              .filter((item: AppNS.ProjectLead) => item.job)
              .map((item: AppNS.ProjectLead) => {
                return parseProjectLead(item);
              }),
          });
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiDeleteEventReport(reqData: AppNS.Params): Promise<string> {
  const req = {
    id: reqData.id,
    properties: {
      state: 'cancelled_by_agency',
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${EVENTS_URL}/cancel.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

function apiGetBookings(
  searchQuery: string,
  states: string | Array<string>,
  page: number,
  searchBy: string,
  projectType: string,
  agencyCompanyId: string
): Promise<AppNS.GetProjectLeadsApiResponse> {
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${BOOKINGS_URL}`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
        params: {
          query: searchQuery,
          search_by: searchBy,
          job_type: projectType,
          states,
          agency_company_id: agencyCompanyId,
          page,
        },
      })
      .then(({ data }) => {
        if (data.results) {
          resolve({
            ...data,
            results: data.results.map((item: AppNS.ProjectLead) => {
              return parseProjectLead(item);
            }),
          });
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetProjectLeadById(leadId: string): Promise<AppNS.ProjectLead> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${LEADS_URL}/${leadId}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(parseProjectLead(data.model));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetJobBookingById(bookingId: string): Promise<AppNS.ProjectLead> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${BOOKINGS_URL}/${bookingId}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(parseProjectLead(data.model));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSetLeadState(reqData: AppNS.Params): Promise<AppNS.ProjectLeadState> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${LEADS_URL}/${STATE_URL}.json`, reqData)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.properties.state);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiCreateProposal(reqData: AppNS.LeadProposal): Promise<boolean> {
  const data = parseProposal(reqData);

  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${LEADS_URL}.json`, data)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.properties.state);
        } else {
          reject(data);
        }
      })
      .catch((error) => {
        reject(error.response.data);
      });
  });
}

export function apiCancelJob(reqData: AppNS.Params, requestOnly: boolean): Promise<string> {
  const data = {
    id: reqData.id,
    properties: {
      cancellationMessage: reqData.message || 'Cancelled by agency',
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${URL}/${requestOnly ? 'request-cancellation.json' : 'cancel.json'}`, data)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiReopenJob(reqData: AppNS.Params): Promise<AppNS.Params> {
  const data = {
    id: reqData.id,
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${URL}/reopen.json`, data)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiAddJobSpecialDocument(reqData: ApiNS.AddJobSpecialDocumentRequest): Promise<AppNS.Params> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/${JOB_DOCUMENTS}.json`, reqData)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        reject(error.response.data);
      });
  });
}

export function apiCreateEventReport(reqData: ApiNS.AddEventRequestData): Promise<AppNS.JobEventReport> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/${EVENTS_URL}.json`, prepareEventReportData(reqData))
      .then(({ data }) => {
        if (data.model) {
          resolve(parseNewEventResponse(data.model));
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}

export function apiCreateBooking(reqData: ApiNS.AddBookingRequestData): Promise<string> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/${CHECKOUTS}`, prepareBookingData(reqData))
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}

export function apiGetCheckoutState(checkoutId: string): Promise<AppNS.Params> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${CHECKOUTS}/${checkoutId}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}

export function apiAcceptPermit(id: string): Promise<AppNS.JobChange> {
  const data = {
    id,
    properties: {
      state: 'accepted',
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${JOB_CHANGES_URL}.json`, data)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}

export function apiGetJobChange(id: string): Promise<AppNS.JobChange> {
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${JOB_CHANGES_URL}/${id}.json`)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}

export function apiRequestCompletePermit(id: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${REQUEST_CLOSING_URL}.json`, { id })
      .then(({ data }) => {
        if (data.model) {
          resolve(true);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiRejectPermit(id: string): Promise<boolean> {
  const data = {
    id,
    properties: {
      state: 'rejected',
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${JOB_CHANGES_URL}.json`, data)
      .then(({ data }) => {
        if (data.model) {
          resolve(true);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}
